import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import { apiDelete, apiGet, apiPost } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { User } from '../types/user';

interface AddUserForm {
  email: string;
  password: string;
  role: 'admin' | 'editor';
}

const EMPTY_ADD_FORM: AddUserForm = { email: '', password: '', role: 'editor' };

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [listError, setListError] = useState<string | null>(null);

  // Add-user form state
  const [addForm, setAddForm] = useState<AddUserForm>(EMPTY_ADD_FORM);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState(false);

  // Delete confirmation dialog
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Reset-password dialog
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  const loadUsers = useCallback(async () => {
    setListError(null);
    try {
      const res = await apiGet('/api/admin/users');
      if (res.ok) {
        const data: User[] = await res.json();
        setUsers(data);
      } else {
        setListError(`Failed to load users (HTTP ${res.status})`);
      }
    } catch {
      setListError('Network error while loading users.');
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Role gate — editors land here via direct URL; show a 403 message.
  if (currentUser?.role !== 'admin') {
    return (
      <Box p={4} textAlign="center">
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Box>
    );
  }

  // ── Add user ──────────────────────────────────────────────────────────────

  const handleAddChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRoleChange = (e: SelectChangeEvent) => {
    setAddForm((prev) => ({
      ...prev,
      role: e.target.value as 'admin' | 'editor',
    }));
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);
    setAddSuccess(false);

    try {
      const res = await apiPost('/api/admin/users', addForm);
      if (res.status === 409) {
        setAddError('This email is already registered.');
        return;
      }
      if (!res.ok) {
        setAddError(`Failed to create user (HTTP ${res.status})`);
        return;
      }
      setAddForm(EMPTY_ADD_FORM);
      setAddSuccess(true);
      await loadUsers();
    } catch {
      setAddError('Network error while creating user.');
    }
  };

  // ── Delete user ───────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);

    try {
      const res = await apiDelete(`/api/admin/users/${deleteTarget.id}`);
      if (!res.ok) {
        setDeleteError(`Failed to delete user (HTTP ${res.status})`);
        return;
      }
      setDeleteTarget(null);
      await loadUsers();
    } catch {
      setDeleteError('Network error while deleting user.');
    }
  };

  // ── Reset password ────────────────────────────────────────────────────────

  const handleResetSubmit = async () => {
    if (!resetTarget) return;
    setResetError(null);
    setResetSuccess(false);

    try {
      const res = await apiPost(
        `/api/admin/users/${resetTarget.id}/reset-password`,
        { new_password: newPassword }
      );
      if (!res.ok) {
        setResetError(`Failed to reset password (HTTP ${res.status})`);
        return;
      }
      setResetSuccess(true);
      setNewPassword('');
      setResetTarget(null);
    } catch {
      setResetError('Network error while resetting password.');
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h5" component="h2" gutterBottom>
        User Management
      </Typography>

      {listError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
        </Alert>
      )}

      {/* ── User table ── */}
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.role}</TableCell>
                <TableCell>
                  {new Date(u.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Reset password">
                    <IconButton onClick={() => setResetTarget(u)}>
                      <LockResetIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete user">
                    <IconButton
                      color="error"
                      onClick={() => setDeleteTarget(u)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Add user form ── */}
      <Typography variant="h6" gutterBottom>
        Add user
      </Typography>

      {addSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          User created successfully.
        </Alert>
      )}
      {addError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {addError}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleAddSubmit}
        sx={{ maxWidth: 480 }}
      >
        <Stack spacing={2}>
          <TextField
            label="Email"
            name="email"
            type="email"
            value={addForm.email}
            onChange={handleAddChange}
            required
            fullWidth
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            value={addForm.password}
            onChange={handleAddChange}
            required
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="add-role-label">Role</InputLabel>
            <Select
              labelId="add-role-label"
              value={addForm.role}
              label="Role"
              onChange={handleAddRoleChange}
            >
              <MenuItem value="editor">editor</MenuItem>
              <MenuItem value="admin">admin</MenuItem>
            </Select>
          </FormControl>
          <Button type="submit" variant="contained">
            Create user
          </Button>
        </Stack>
      </Box>

      {/* ── Delete confirmation dialog ── */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Delete user</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>{deleteTarget?.email}</strong>? This action cannot be undone.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reset password dialog ── */}
      <Dialog
        open={resetTarget !== null}
        onClose={() => {
          setResetTarget(null);
          setNewPassword('');
          setResetError(null);
          setResetSuccess(false);
        }}
      >
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Set a new password for <strong>{resetTarget?.email}</strong>.
          </DialogContentText>
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            required
            autoFocus
          />
          {resetError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {resetError}
            </Alert>
          )}
          {resetSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Password reset successfully.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setResetTarget(null);
              setNewPassword('');
              setResetError(null);
              setResetSuccess(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleResetSubmit}
            disabled={!newPassword}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
